// Herrell Interactive — Master Hub app registry
export const APPS = [
  {
    id: "apiarykeeper",
    slug: "beekeeper",
    name: "ApiaryKeeper\u2122",
    tagline: "Intelligent Beekeeping Assistant",
    category: "Agriculture",
    description: "Track hives, inspections, harvests and colony health across your apiary.",
    longDescription:
      "ApiaryKeeper is your intelligent beekeeping companion — combining AI-assisted identification, interactive queen genetics and a live yard map so you can manage every colony with confidence, from a single backyard hive to a commercial operation.",
    logo: "/logos/apiarykeeper.png",
    url: "https://bee-yard-hub.emergent.host",
    screenshots: [
      "/screenshots/ak6.webp",
      "/screenshots/ak1.webp",
      "/screenshots/ak7.webp",
      "/screenshots/ak8.webp",
      "/screenshots/ak2.webp",
      "/screenshots/ak3.webp",
      "/screenshots/ak4.webp",
      "/screenshots/ak5.webp",
      "/screenshots/ak9.webp",
    ],
    framedScreenshots: false,
    stores: {
      playStore: "https://play.google.com/store/apps/details?id=com.apiarykeeper.myapp",
      playTesting: "https://play.google.com/apps/testing/com.apiarykeeper.myapp",
    },
    accent: "#c9a24b",
    live: true,
    beta: true,
    features: [
      { name: "AI-assisted identification", desc: "Identify pests and diseases from a photo with helpful guidance.", pro: true },
      { name: "3D Genetics", desc: "Interactive 5-generation queen pedigree tree." },
      { name: "Yard Management", desc: "Visual map with status-alert rings (Red/Green)." },
    ],
  },
  {
    id: "mileagekeeper",
    slug: "mileagekeeper",
    name: "MileageKeeper\u2122",
    tagline: "Strategic Mileage & Tax Management",
    category: "Small Business",
    description: "Log trips, fuel and expenses for accurate, audit-ready mileage records.",
    longDescription:
      "MileageKeeper turns everyday driving into audit-ready tax records. Forecast fuel costs, manage multiple businesses in one secure ledger and let AI OCR pull the numbers straight off your receipts.",
    logo: "/logos/mileagekeeper.png",
    url: "https://fuel-audit-1.emergent.host",
    screenshots: [
      "/screenshots/mk1.png",
      "/screenshots/mk5.png",
      "/screenshots/mk4.png",
      "/screenshots/mk2.png",
      "/screenshots/mk3.png",
    ],
    framedScreenshots: true,
    stores: {
      playStore: "https://play.google.com/store/apps/details?id=com.mileagekeeper.myapp",
      playTesting: "https://play.google.com/apps/testing/com.mileagekeeper.myapp",
    },
    accent: "#4e7d3a",
    live: true,
    beta: true,
    features: [
      { name: "Fuel Planner\u2122", desc: "Forecast trip costs and find truck-friendly fuel stops.", pro: true },
      { name: "Multi-Business Ledger", desc: "Manage multiple LLCs in one secure stream." },
      { name: "Receipt Vault", desc: "AI OCR that extracts data from gas and repair receipts." },
    ],
  },
  {
    id: "evidencekeeper",
    slug: "evidencekeeper",
    name: "EvidenceKeeper\u2122",
    tagline: "Objective Documentation Assistant",
    category: "Everyday Life",
    description: "A secure vault for documents and evidence you need to keep and protect.",
    longDescription:
      "EvidenceKeeper turns your story into observable facts, preserves your evidence with strong encryption and produces organized, professional reports — a calm, private record of what actually happened.",
    logo: "/logos/evidencekeeper.png",
    url: "https://doc-coach.emergent.host",
    screenshots: [
      "/screenshots/ek_land.png",
      "/screenshots/ek_signin.png",
      "/screenshots/ek_home.png",
      "/screenshots/ek_matter.png",
      "/screenshots/ek_state.png",
    ],
    framedScreenshots: true,
    accent: "#3f6fb0",
    live: true,
    beta: true,
    features: [
      { name: "Fact Filter\u2122", desc: "AI Coach that separates feelings from observable facts.", pro: true },
      { name: "Secure Vault", desc: "Bank-level encryption with SHA-256 integrity hashing." },
      { name: "Legal Reports", desc: "Organized, professional PDF chronologies." },
    ],
  },
  {
    id: "greenkeep",
    slug: "greenkeeper",
    name: "GreenKeep\u2122",
    tagline: "Sustainable Land & Garden Management",
    category: "Agriculture",
    description: "Plan gardens and orchards, watering, tasks and harvests to grow more.",
    longDescription:
      "GreenKeep helps you steward your land sustainably — AI plant-health advice, real-time companion planting maps and a drag-and-drop irrigation planner for healthier harvests, season after season.",
    logo: "/logos/greenkeeper.png",
    url: "",
    accent: "#4e7d3a",
    live: false,
    launch: "Late 2026",
    features: [
      { name: "Ask a Leaf\u2122 AI", desc: "Companion-led advice for pest and plant health.", pro: true },
      { name: "Companion Planting", desc: "Real-time AI mapping for healthier harvests." },
      { name: "Irrigation Map", desc: "Drag-and-drop layer for buried water lines and zones." },
    ],
  },
  {
    id: "flockkeeper",
    slug: "flockkeeper",
    name: "FlockKeeper\u2122",
    tagline: "Professional Poultry Management",
    category: "Agriculture",
    description: "Poultry and livestock management to keep every flock healthy and thriving.",
    longDescription:
      "FlockKeeper is professional-grade poultry management — instant AI health guidance from a photo, daily egg intelligence and real-time predator alerts to keep every flock safe and thriving.",
    logo: "/logos/flockkeeper.png",
    url: "",
    accent: "#4e7d3a",
    live: false,
    launch: "Late 2026",
    features: [
      { name: "Flock Doctor\u2122 AI", desc: "Instant poultry health guidance from photos.", pro: true },
      { name: "Egg Intelligence", desc: "Daily yield tracking and quality trend analysis." },
      { name: "Predator Alerts", desc: "Real-time activity heatmaps for property safety." },
    ],
  },
  {
    id: "farmstand",
    slug: "farmstand",
    name: "FarmStand Checkout",
    tagline: "Scan · Shop · Support Local",
    category: "Small Business",
    description: "Scan, price and check out farm-stand goods — supporting local, made simple.",
    longDescription:
      "FarmStand Checkout makes selling from your farm stand effortless — scan, price and check out fresh goods so customers can support local with a tap.",
    logo: "/logos/farmstand.png",
    url: "",
    accent: "#c9a24b",
    live: false,
    launch: "Early 2027",
    features: [
      { name: "Barcode & QR Scanning", desc: "Scan products for fast, accurate pricing at the stand." },
      { name: "Inventory Tracking", desc: "Keep stock counts current across your stand." },
      { name: "Daily Totals", desc: "See clear sales summaries at the end of each day." },
    ],
  },
  {
    id: "theologia",
    slug: "theologia",
    name: "Theologia",
    tagline: "Scripture Study & Theology",
    category: "Everyday Life",
    description: "Study Scripture with original languages, references and deep theology tools.",
    longDescription:
      "Theologia is a deep-study companion for Scripture — original languages, cross-references and rich theological tools to help you go deeper in the Word.",
    logo: "/logos/theologia.png",
    url: "",
    accent: "#c9a24b",
    live: false,
    launch: "Early 2027",
    features: [
      { name: "Original-Language Tools", desc: "Explore Hebrew and Greek word studies with ease." },
      { name: "Cross-References", desc: "Follow themes and passages across all of Scripture." },
      { name: "Study Notes", desc: "Save and organize your notes by passage." },
    ],
  },
  {
    id: "homesteadkeeper",
    slug: "homesteadkeeper",
    name: "HomesteadKeeper",
    tagline: "Whole-Homestead Management",
    category: "Home",
    description: "One dashboard for the whole homestead — tasks, animals, land and more.",
    longDescription:
      "HomesteadKeeper brings the whole homestead into one calm dashboard — tasks, animals, land and everything in between, so nothing slips through the cracks.",
    logo: "/logos/homesteadkeeper.png",
    url: "",
    accent: "#c9a24b",
    live: false,
    launch: "Mid 2027",
    features: [
      { name: "Unified Dashboard", desc: "Tasks, animals and land organized in one place." },
      { name: "Seasonal Planning", desc: "Plan and track work across the whole year." },
      { name: "Records & Reminders", desc: "Keep important dates and logs in order." },
    ],
  },
  {
    id: "momvault",
    slug: "momvault",
    name: "MomVault",
    tagline: "Family Memories, Safely Kept",
    category: "Family",
    description: "A heartfelt, private vault for family memories, milestones and keepsakes.",
    longDescription:
      "MomVault is a heartfelt, private vault for the moments that matter — memories, milestones and keepsakes, safely kept for the whole family.",
    logo: "/logos/momvault.png",
    url: "",
    accent: "#b98bb0",
    live: false,
    launch: "Mid 2027",
    features: [
      { name: "Private Memory Vault", desc: "A secure home for photos and milestones." },
      { name: "Organized Timelines", desc: "Group keepsakes by child and by year." },
      { name: "Easy Sharing", desc: "Share selected memories with close family." },
    ],
  },
];

export const LIVE_APPS = APPS.filter((a) => a.live);
export const ROADMAP_APPS = APPS.filter((a) => !a.live);
export const getAppBySlug = (slug) => APPS.find((a) => a.slug === slug);
export const cleanName = (n) => n.replace(/\u2122/g, "").trim();

export const storeLinks = (name) => ({
  appStore: `https://apps.apple.com/us/search?term=${encodeURIComponent(name)}`,
  playStore: `https://play.google.com/store/search?q=${encodeURIComponent(name)}&c=apps`,
});

export const getStores = (app) => {
  const name = cleanName(app.name);
  const s = app.stores || {};
  return {
    appStore: s.appStore || `https://apps.apple.com/us/search?term=${encodeURIComponent(name)}`,
    playStore: s.playStore || `https://play.google.com/store/search?q=${encodeURIComponent(name)}&c=apps`,
    playTesting: s.playTesting || null,
    hasStore: Boolean(s.appStore || s.playStore),
  };
};
